import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, apiRequest } from "../../api/client";
import type { ConferenceDictionaries, ConferenceDictionaryStats, GlossaryPreview } from "../../api/types";
import { KindBadge } from "./DictionariesPage";

/**
 * Which dictionaries a conference translates with, in priority order.
 *   1. AFA central dictionaries — on by default, highest priority; can be switched off per conference
 *   2. the dictionaries below, top to bottom
 *   3. anything else: the general translation model
 * Every change is saved at once and applies to running sessions on their next sentence.
 */
export default function ConferenceDictionariesPanel({ conferenceId }: { conferenceId: string }) {
  const [data, setData] = useState<ConferenceDictionaries | null>(null);
  const [pick, setPick] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<GlossaryPreview | null>(null);
  const [previewQuery, setPreviewQuery] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [stats, setStats] = useState<ConferenceDictionaryStats | null>(null);
  const [showStats, setShowStats] = useState(false);

  async function load() {
    setData(await apiRequest<ConferenceDictionaries>(`/conferences/${conferenceId}/dictionaries`));
  }
  useEffect(() => {
    void load().catch(() => setError("Could not load this conference's dictionaries"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conferenceId]);

  async function save(ids: string[], useCentralDictionaries?: boolean) {
    setSaving(true);
    setError(null);
    try {
      setData(
        await apiRequest<ConferenceDictionaries>(`/conferences/${conferenceId}/dictionaries`, {
          method: "PUT",
          body: { dictionaryIds: ids, useCentralDictionaries },
        }),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save");
    } finally {
      setSaving(false);
    }
  }

  const ids = data?.assigned.map((d) => d.id) ?? [];
  const move = (index: number, delta: number) => {
    const next = [...ids];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    void save(next);
  };

  // Preview: reload when opened and (debounced) when searching.
  useEffect(() => {
    if (!showPreview) return;
    const timer = setTimeout(() => {
      void apiRequest<GlossaryPreview>(`/conferences/${conferenceId}/dictionaries/preview`, { query: { q: previewQuery.trim() || undefined } }).then(setPreview);
    }, 200);
    return () => clearTimeout(timer);
  }, [showPreview, previewQuery, conferenceId, data]);

  useEffect(() => {
    if (showStats) void apiRequest<ConferenceDictionaryStats>(`/conferences/${conferenceId}/dictionaries/stats`).then(setStats).catch(() => setStats(null));
  }, [showStats, conferenceId]);

  if (!data) return error ? <div className="error-banner">{error}</div> : null;

  return (
    <div className="card" style={{ marginBottom: 18 }}>
      <div className="toolbar" style={{ marginBottom: 6 }}>
        <strong>Dictionaries for this conference</strong>
        <div style={{ flex: 1 }} />
        <button className="btn-secondary" onClick={() => setShowStats(true)}>Usage</button>
        <button className="btn-secondary" onClick={() => { setPreviewQuery(""); setShowPreview(true); }}>Preview loaded glossary</button>
      </div>
      <p style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 0 }}>
        Translation uses these in order — the first dictionary that has a term decides its wording. Anything they don't
        cover is translated by the general translation model. {saving && <em>Saving…</em>}
      </p>

      <div className="toolbar" style={{ marginBottom: 6, gap: 10 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={data.useCentralDictionaries}
            disabled={saving}
            onChange={(e) => void save(ids, e.target.checked)}
          />
          Use AFA Standard Terms Dictionary
        </label>
        <span style={{ color: "var(--text-muted)", fontSize: 12.5 }}>
          {data.useCentralDictionaries
            ? "On — approved AFA terms override the translation model for this conference."
            : "Off — this conference bypasses the AFA dictionary entirely; the translation model decides all wording."}
        </span>
      </div>

      <ol style={{ margin: 0, paddingInlineStart: 0, listStyle: "none" }} className="stack">
        {data.central.map((d) => (
          <li key={d.id} style={{ ...rowStyle, opacity: data.useCentralDictionaries ? 1 : 0.5 }}>
            <span style={{ width: 22 }} title={data.useCentralDictionaries ? "Always loaded" : "Bypassed for this conference"}>
              {data.useCentralDictionaries ? "🔒" : "🚫"}
            </span>
            <Link to={`/operator/dictionaries/${d.id}`} style={{ fontWeight: 700 }}>{d.name}</Link>
            <KindBadge kind={d.kind} />
            <span style={{ color: "var(--text-muted)", fontSize: 13 }}>
              {d.termCount} terms · {data.useCentralDictionaries ? "always loaded, highest priority" : "bypassed"}
            </span>
          </li>
        ))}
        {data.assigned.map((d, i) => (
          <li key={d.id} style={rowStyle}>
            <span style={{ width: 22, fontWeight: 700 }}>{i + 1}</span>
            <Link to={`/operator/dictionaries/${d.id}`} style={{ fontWeight: 700 }}>{d.name}</Link>
            <KindBadge kind={d.kind} />
            <span style={{ color: "var(--text-muted)", fontSize: 13, flex: 1 }}>{d.termCount} terms</span>
            <button className="btn-ghost" disabled={saving || i === 0} onClick={() => move(i, -1)} aria-label="Higher priority">▲</button>
            <button className="btn-ghost" disabled={saving || i === data.assigned.length - 1} onClick={() => move(i, 1)} aria-label="Lower priority">▼</button>
            <button className="btn-ghost" disabled={saving} onClick={() => void save(ids.filter((x) => x !== d.id))}>Remove</button>
          </li>
        ))}
        <li style={{ ...rowStyle, borderStyle: "dashed" }}>
          <span style={{ width: 22 }}>🌐</span>
          <span style={{ color: "var(--text-muted)" }}>General translation model — everything not covered above</span>
        </li>
      </ol>

      <div className="toolbar" style={{ marginTop: 12 }}>
        <select value={pick} onChange={(e) => setPick(e.target.value)} style={{ minWidth: 260 }}>
          <option value="">Add a dictionary…</option>
          {data.available.map((d) => (
            <option key={d.id} value={d.id}>{d.name} ({d.termCount} terms)</option>
          ))}
        </select>
        <button className="btn-primary" disabled={!pick || saving} onClick={() => { void save([...ids, pick]); setPick(""); }}>
          Add
        </button>
        <Link className="btn-ghost" to="/operator/dictionaries">Manage dictionaries</Link>
      </div>
      {error && <div className="error-banner" style={{ marginTop: 10 }}>{error}</div>}

      {showPreview && (
        <div className="modal-backdrop" onClick={() => setShowPreview(false)}>
          <div className="modal" style={{ width: "min(980px, 94vw)", maxHeight: "86vh", overflow: "auto" }} onClick={(e) => e.stopPropagation()}>
            <h2>Loaded glossary</h2>
            {!preview ? "Loading…" : (
              <div className="stack">
                <div style={{ fontSize: 13.5 }}>
                  {preview.dictionaries.map((d) => `${d.layer + 1}. ${d.name} (${d.terms})`).join("  →  ")}
                  {"  →  general translation model"}
                </div>
                <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
                  {preview.totalEntries} entries; {preview.overriddenEntries} are overridden by a higher-priority dictionary (greyed out).
                </div>
                <input placeholder="Search…" value={previewQuery} onChange={(e) => setPreviewQuery(e.target.value)} style={{ padding: "9px 12px", borderRadius: 8, border: "1px solid var(--border)" }} />
                <table className="table">
                  <thead><tr><th>#</th><th>Dictionary</th><th>English</th><th>Arabic</th><th>French</th></tr></thead>
                  <tbody>
                    {preview.entries.map((e, i) => {
                      const aliases = [...(e.aliases.en ?? []), ...(e.aliases.ar ?? []), ...(e.aliases.fr ?? [])];
                      return (
                        <tr key={i} style={e.overriddenBy ? { opacity: 0.5 } : undefined}>
                          <td>{e.layer + 1}</td>
                          <td>{e.dictionary}{e.overriddenBy && <div style={{ fontSize: 12 }}>overridden by {e.overriddenBy}</div>}</td>
                          <td>{e.en}{aliases.length > 0 && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>also: {aliases.join(" · ")}</div>}</td>
                          <td dir="rtl">{e.ar}</td>
                          <td>{e.fr}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {preview.truncated && <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Showing the first {preview.entries.length}. Search to narrow it down.</div>}
              </div>
            )}
            <div className="modal-actions"><button className="btn-primary" onClick={() => setShowPreview(false)}>Close</button></div>
          </div>
        </div>
      )}

      {showStats && (
        <div className="modal-backdrop" onClick={() => setShowStats(false)}>
          <div className="modal" style={{ width: "min(760px, 94vw)", maxHeight: "86vh", overflow: "auto" }} onClick={(e) => e.stopPropagation()}>
            <h2>Dictionary usage in this conference</h2>
            {!stats ? "Loading…" : stats.totalHits === 0 ? (
              <p>Nothing yet — counts appear after a session of this conference has run.</p>
            ) : (
              <div className="stack">
                <div><strong>{stats.totalHits}</strong> approved terms were used instead of machine translation.</div>
                <div style={{ fontSize: 13 }}>By dictionary: {stats.byDictionary.map((d) => `${d.name} (${d.hits})`).join(" · ")}</div>
                <table className="table">
                  <thead><tr><th>Term</th><th>Arabic</th><th>Dictionary</th><th>Times</th></tr></thead>
                  <tbody>
                    {stats.topTerms.map((t, i) => (
                      <tr key={i}><td>{t.en}</td><td dir="rtl">{t.ar}</td><td>{t.dictionary}</td><td>{t.hits}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="modal-actions"><button className="btn-primary" onClick={() => setShowStats(false)}>Close</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

const rowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "8px 12px",
  border: "1px solid var(--border)",
  borderRadius: 10,
  background: "var(--surface)",
};
